import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import GardenWizard from "./pages/GardenWizard";
import Dashboard from "./pages/Dashboard";
import PlanDetail from "./pages/PlanDetail";
import SavedPlans from "./pages/SavedPlans";
import CalendarPage from "./pages/CalendarPage";
import RemindersPage from "./pages/RemindersPage";
import ShareablePlan from "./pages/ShareablePlan";
import PricingPage from "./pages/PricingPage";
import AuthCallback from "./pages/AuthCallback";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import ProfilePage from "./pages/ProfilePage";
import PlantIdentifier from "./pages/PlantIdentifier";
import JournalPage from "./pages/JournalPage";
import SeedStockPage from "./pages/SeedStockPage";
import ProGardenerChat from "./pages/ProGardenerChat";
import AdminPage from "./pages/AdminPage";
import MarketplacePage from "./pages/MarketplacePage";
import SellSeedsPage from "./pages/SellSeedsPage";
import SeedPacketTemplate from "./pages/SeedPacketTemplate";
import SellerWalletPage from "./pages/SellerWalletPage";
import GardenPlannerPage from "./pages/GardenPlannerPage";
import WeatherPage from "./pages/WeatherPage";
import PlantDatabasePage from "./pages/PlantDatabasePage";
import ContactPage from "./pages/ContactPage";
import PlantingCalendarPage from "./pages/PlantingCalendarPage";
import VisualDesignTool from "./pages/VisualDesignTool";
import SharedDesignPage from "./pages/SharedDesignPage";
import GamificationPage from "./pages/GamificationPage";
import ChallengesPage from "./pages/ChallengesPage";
import TeacherRegisterPage from "./pages/TeacherRegisterPage";
import TeacherDashboard from "./pages/TeacherDashboard";
import EducatorPage from "./pages/EducatorPage";
import GrowthToolPage from "./pages/GrowthToolPage";
import ProBlogPage from "./pages/ProBlogPage";
import PersonalAlmanacPage from "./pages/PersonalAlmanacPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import { LanguageProvider } from "./contexts/LanguageContext";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Build version: v2.0.1 - Pro Gardener Chat + Plant ID UX update
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, requiredTier = null }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requiredTier) {
    const tierOrder = { free: 0, standard: 1, premium: 2 };
    if (tierOrder[user.subscription_tier] < tierOrder[requiredTier]) {
      return <Navigate to="/pricing" replace />;
    }
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<Navigate to="/pricing" replace />} />
      <Route path="/create" element={<GardenWizard />} />
      <Route path="/dashboard/:planId" element={<Dashboard />} />
      <Route path="/plan/:planId" element={<PlanDetail />} />
      <Route path="/planner/:planId" element={<GardenPlannerPage />} />
      <Route path="/saved" element={<SavedPlans />} />
      <Route path="/calendar/:planId" element={<CalendarPage />} />
      <Route path="/reminders/:planId" element={<RemindersPage />} />
      <Route path="/share/:planId" element={<ShareablePlan />} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/identify" element={<PlantIdentifier />} />
      <Route path="/journal" element={<ProtectedRoute requiredTier="standard"><JournalPage /></ProtectedRoute>} />
      <Route path="/seed-stock" element={<ProtectedRoute requiredTier="standard"><SeedStockPage /></ProtectedRoute>} />
      <Route path="/pro-chat" element={<ProtectedRoute requiredTier="premium"><ProGardenerChat /></ProtectedRoute>} />
      
      {/* Admin */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Marketplace */}
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/sell" element={<SellSeedsPage />} />
      <Route path="/marketplace/seed-packet" element={<SeedPacketTemplate />} />
      <Route path="/marketplace/wallet" element={<SellerWalletPage />} />
      
      {/* Payment */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* New Features */}
      <Route path="/weather" element={<WeatherPage />} />
      <Route path="/plants" element={<PlantDatabasePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/calendar" element={<PlantingCalendarPage />} />
      <Route path="/design" element={<VisualDesignTool />} />
      <Route path="/shared/:shareId" element={<SharedDesignPage />} />
      <Route path="/learn" element={<GamificationPage />} />
      <Route path="/challenges" element={<ChallengesPage />} />
      <Route path="/challenges/teacher-register" element={<TeacherRegisterPage />} />
      <Route path="/teacher" element={<TeacherDashboard />} />
      <Route path="/educators" element={<EducatorPage />} />
      <Route path="/growth-tool" element={<GrowthToolPage />} />
      <Route path="/blog" element={<ProBlogPage />} />
      <Route path="/almanac" element={<PersonalAlmanacPage />} />
    </Routes>
  );
}

import PricingPopup from "./components/PricingPopup";

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <div className="grain-overlay" />
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <PricingPopup />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
